// ======================================================================
// 📁 File: src/modules/ticket/pages/TicketDashboard.jsx
// Purpose: Ticket Dashboard (Improved UI + Status Chips + Row click)
// Updated: 2025-12-30  — UI upgrade + correct card counts
// ======================================================================

import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../api/axiosClient";
import { AuthContext } from "../../../context/AuthContext";
import "../ticketStyles.css";

// ⭐ ADD THIS ONE NEW IMPORT (SAFE)
import TicketAIContainer from "../components/ai/TicketAIContainer";

// ----------------------------
// Card Component
// ----------------------------
function StatCard({ label, value, color }) {
  return (
    <div
      className="p-3 rounded-4 shadow-sm"
      style={{
        background: color,
        color: "white",
        flex: 1,
        minWidth: 160,
      }}
    >
      <div style={{ fontSize: 14, opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

// ----------------------------
// Status Chip Component
// ----------------------------
function StatusChip({ status }) {
  const s = String(status || "").toUpperCase();
  let bg = "#6c757d";

  if (s === "OPEN") bg = "#6A00F4";
  if (s === "IN_PROGRESS") bg = "#fabb00";
  if (s === "RESOLVED") bg = "#00b96b";
  if (s === "CLOSED") bg = "#495057";
  if (s === "ASSIGNED") bg = "#0096d2";

  return (
    <span
      style={{
        background: bg,
        color: "white",
        fontSize: 12,
        padding: "4px 10px",
        borderRadius: 20,
        fontWeight: 600,
      }}
    >
      {s.replace("_", " ")}
    </span>
  );
}

function TicketDashboard() {
  const { activeScope } = useContext(AuthContext);
  const navigate = useNavigate();

  const tenant_id = activeScope?.tenant_id;
  const location_id = activeScope?.location_id;

  const [cards, setCards] = useState({
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
    total_today: 0,
  });

  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / pageSize) : 1),
    [total]
  );

  // -----------------------------------
  // Load stats & list
  // -----------------------------------
  useEffect(() => {
    if (!tenant_id || !location_id) return;
    fetchStats();
    fetchTickets(page);
    // eslint-disable-next-line
  }, [tenant_id, location_id]);

  useEffect(() => {
    if (!tenant_id || !location_id) return;
    fetchTickets(page);
    // eslint-disable-next-line
  }, [page, searchText, statusFilter]);

  // -----------------------------------
  // API: Dashboard stats (FIXED)
  // -----------------------------------
  async function fetchStats() {
    try {
      const baseScope = { tenant_id, location_id };

      const [summaryRes, ticketsRes] = await Promise.all([
        axiosClient.get("/ticket-management/dashboard/summary", {
          params: baseScope,
        }),
        axiosClient.get("/ticket-management/tickets", {
          params: { ...baseScope, page: 1, page_size: 500 },
        }),
      ]);

      const summary = summaryRes?.data?.data || {};
      const listData = ticketsRes?.data?.data || {};
      const items = listData.items || [];

      let open = 0,
        inProgress = 0,
        resolved = 0,
        closed = 0,
        assigned = 0;

      items.forEach((t) => {
        const s = String(t.status || "").toUpperCase();
        if (s === "OPEN") open += 1;
        else if (s === "IN_PROGRESS") inProgress += 1;
        else if (s === "RESOLVED") resolved += 1;
        else if (s === "CLOSED") closed += 1;
        else if (s === "ASSIGNED") assigned += 1;
      });

      const computedHasData =
        open || inProgress || resolved || closed || assigned;

      const cardOpen = computedHasData
        ? open + assigned
        : (summary.open || summary.OPEN || 0) +
          (summary.assigned || summary.ASSIGNED || 0);

      const cardInProgress = computedHasData
        ? inProgress
        : summary.in_progress || summary.IN_PROGRESS || 0;

      const cardResolved = computedHasData
        ? resolved
        : summary.resolved || summary.RESOLVED || 0;

      const cardClosed = computedHasData
        ? closed
        : summary.closed || summary.CLOSED || 0;

      const cardNewToday =
        summary.new_today || summary.total_today || 0;

      setCards({
        open: cardOpen,
        in_progress: cardInProgress,
        resolved: cardResolved,
        closed: cardClosed,
        total_today: cardNewToday,
      });
    } catch (err) {
      console.error("fetchStats error:", err);
    }
  }

  // -----------------------------------
  // API: Tickets list
  // -----------------------------------
  async function fetchTickets(pageToFetch = 1) {
    try {
      setLoading(true);

      const params = {
        tenant_id,
        location_id,
        page: pageToFetch,
        page_size: pageSize,
      };
      if (searchText) params.q = searchText;
      if (statusFilter) params.status = statusFilter;

      const res = await axiosClient.get("/ticket-management/tickets", {
        params,
      });

      const data = res?.data?.data || {};
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || pageToFetch);
    } catch (err) {
      console.error("fetchTickets error:", err);
    } finally {
      setLoading(false);
    }
  }

  // -----------------------------------
  // Render
  // -----------------------------------
  if (!tenant_id || !location_id) {
    return (
      <div className="container py-5 text-danger">
        Tenant / Location missing in client scope.
      </div>
    );
  }

  return (
    <div className="tf-page-wrapper">
      <div className="tf-page-container container-fluid px-4">
        {/* Header */}
        <div
          className="mb-4 px-4 py-3 text-white d-flex justify-content-between align-items-center"
          style={{
            borderRadius: "18px",
            background: "linear-gradient(90deg,#6A00F4 0%, #5200CC 100%)",
          }}
        >
          <div>
            <h3 className="mb-0 fw-bold">Ticket Dashboard</h3>
            <small className="text-light">
              Real-time ticket activity and overview.
            </small>
          </div>

          <div>
            <button
              className="btn btn-light btn-sm fw-semibold"
              onClick={() => navigate("/tickets/create")}
            >
              + Create Ticket
            </button>

            <button
              className="btn btn-outline-light btn-sm fw-semibold ms-2"
              onClick={() => navigate("/tickets/sla-dashboard")}
            >
              SLA Dashboard
            </button>

            {/* ✅ NEW: Performance Dashboard button */}
            <button
              className="btn btn-outline-light btn-sm fw-semibold ms-2"
              onClick={() => navigate("/tickets/performance-dashboard")}
            >
              Performance Dashboard
            </button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="d-flex flex-wrap gap-3 mb-4" style={{ width: "100%" }}>
          <StatCard label="Open" value={cards.open} color="#6A00F4" />
          <StatCard label="In Progress" value={cards.in_progress} color="#FABB00" />
          <StatCard label="Resolved" value={cards.resolved} color="#00B96B" />
          <StatCard label="Closed" value={cards.closed} color="#495057" />
          <StatCard label="New Today" value={cards.total_today} color="#0096D2" />
        </div>

        {/* Filters */}
        <div className="d-flex gap-2 mb-3">
          <input
            className="form-control"
            placeholder="Search tickets..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
            style={{ maxWidth: 300 }}
          />

          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            style={{ maxWidth: 200 }}
          >
            <option value="">All Status</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="CLOSED">Closed</option>
          </select>

          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              setSearchText("");
              setStatusFilter("");
              setPage(1);
            }}
          >
            Clear
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/tickets/view/${t.id}`)}
                >
                  <td style={{ width: 160 }}>{t.ticket_number}</td>
                  <td>{t.title}</td>
                  <td style={{ width: 160 }}>
                    <StatusChip status={t.status} />
                  </td>
                  <td style={{ width: 160 }}>
                    {t.created_at?.substring(0, 10)}
                  </td>
                </tr>
              ))}

              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-muted">
                    No tickets found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            className="btn btn-light"
            disabled={page <= 1}
            onClick={() => setPage((p) => (p > 1 ? p - 1 : p))}
          >
            Previous
          </button>

          <span>
            Page {page} of {totalPages}
          </span>

          <button
            className="btn btn-light"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
          >
            Next
          </button>
        </div>
      </div>

      {/* ⭐ FLOATING AI BUBBLE (SAFE ADDITION) */}
      <TicketAIContainer />
    </div>
  );
}

export default TicketDashboard;
